/**
 * @fileoverview Plans & Documents Upload Modal Component
 *
 * Modal for uploading project documents (floorplans, site plans, other docs)
 * directly to the Projects table attachment fields in Airtable.
 *
 * Upload Flow:
 * 1. User selects project (or uses prefilled project)
 * 2. User selects document category and files
 * 3. Files are uploaded to Cloudinary
 * 4. Cloudinary URLs are sent to Firebase function
 * 5. Firebase function appends attachments to Projects table
 *
 * @requires react
 * @requires react-icons/fa
 * @requires ../../services/cloudinaryService
 * @requires ../../firebase
 * @version 1.1.0
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  FaTimes,
  FaCloudUploadAlt,
  FaFileAlt,
  FaFilePdf,
  FaFileImage,
  FaHome,
  FaUpload,
  FaSpinner,
  FaCheckCircle,
  FaExclamationCircle,
  FaTrash,
  FaFolderOpen,
} from "react-icons/fa";
import * as Sentry from "@sentry/react";
import { uploadFile, buildUploadContext } from "../../services/cloudinaryService";
import { functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import useCurrentTeamMember from "../../hooks/useCurrentTeamMember";
import "./PlansDocsUploadModal.css";

/**
 * Document category options with Airtable field mappings
 * @constant {Array<Object>}
 */
const DOCUMENT_CATEGORIES = [
  {
    value: "floorplans",
    label: "Floorplans",
    description: "Blueprints, floor plans, and architectural drawings",
    icon: FaFileImage,
  },
  {
    value: "siteplans",
    label: "Site Plans",
    description: "Site plans, surveys, soil tests, and environmental reports",
    icon: FaFilePdf,
  },
  {
    value: "other",
    label: "Other Documents",
    description: "Permits, contracts, HOA docs, and other project documents",
    icon: FaFileAlt,
  },
];

/**
 * Accepted file types for document uploads
 * @constant {string}
 */
const ACCEPTED_FILE_TYPES = ".pdf,.jpg,.jpeg,.png";

/**
 * Maximum file size in bytes (10MB)
 * @constant {number}
 */
const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Get appropriate icon for file type
 * @param {string} filename - Name of the file
 * @returns {JSX.Element} Icon component
 */
const getFileIcon = (filename) => {
  const ext = filename.split(".").pop().toLowerCase();
  if (ext === "pdf") return <FaFilePdf className="pdum-file-icon pdf" />;
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
    return <FaFileImage className="pdum-file-icon image" />;
  }
  return <FaFileAlt className="pdum-file-icon doc" />;
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size string
 */
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Plans & Documents Upload Modal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {string} [props.projectId] - Optional Airtable project record ID (prefills & disables selector)
 * @param {string} [props.projectName] - Optional project name for display
 * @param {Function} [props.onUploadComplete] - Callback after successful upload
 * @returns {JSX.Element|null} Modal component or null if not open
 */
const PlansDocsUploadModal = ({
  isOpen,
  onClose,
  projectId: initialProjectId,
  projectName: initialProjectName,
  onUploadComplete,
  showTestAccounts = false,
}) => {
  // Project selection state
  const [selectedProjectId, setSelectedProjectId] = useState(
    initialProjectId || "",
  );
  const [selectedProjectName, setSelectedProjectName] = useState(
    initialProjectName || "",
  );
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState("");

  // Determine if project selector should be disabled (when projectId is passed)
  const isProjectLocked = Boolean(initialProjectId);

  // Canonical RSH team member name for Cloudinary `uploaded_by_name`
  const { name: teamMemberName } = useCurrentTeamMember();

  // File selection state
  const [selectedCategory, setSelectedCategory] = useState("floorplans");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("idle"); // idle, uploading, success, error
  const [uploadProgress, setUploadProgress] = useState({});
  const [errorMessage, setErrorMessage] = useState("");

  /**
   * Sync internal selection from props in both directions: when the parent
   * supplies a project, lock to it; when the parent clears the prop (e.g.,
   * AdminUploads' top selector switched back to "All projects"), reset the
   * dropdown so the modal does not keep stale state.
   */
  useEffect(() => {
    setSelectedProjectId(initialProjectId || "");
    setSelectedProjectName(initialProjectName || "");
  }, [initialProjectId, initialProjectName]);

  /**
   * Fetch active projects for the dropdown
   */
  const fetchActiveProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectsError("");

    try {
      const getActiveProjects = httpsCallable(functions, "getActiveProjects");
      const result = await getActiveProjects({ showTestAccounts });

      if (result.data.success) {
        setProjects(result.data.data || []);
      } else {
        throw new Error(result.data.message || "Failed to fetch projects");
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjectsError("Failed to load projects");
      Sentry.captureException(error);
    } finally {
      setProjectsLoading(false);
    }
  }, [showTestAccounts]);

  /**
   * Fetch active projects when modal opens and no projectId is provided
   */
  useEffect(() => {
    if (isOpen && !isProjectLocked) {
      fetchActiveProjects();
    }
  }, [isOpen, isProjectLocked, fetchActiveProjects]);

  /**
   * Handle project selection change
   * @param {Event} e - Change event
   */
  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setSelectedProjectId(projectId);

    // Find and set project name
    const project = projects.find((p) => p.id === projectId);
    setSelectedProjectName(project?.projectName || project?.name || "");
  };

  /**
   * Validate file before adding
   * @param {File} file - File to validate
   * @returns {Object} Validation result
   */
  const validateFile = (file) => {
    const errors = [];

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      errors.push(`File exceeds 10MB limit (${formatFileSize(file.size)})`);
    }

    // Check file type
    const ext = file.name.split(".").pop().toLowerCase();
    const allowedExts = ["pdf", "jpg", "jpeg", "png"];
    if (!allowedExts.includes(ext)) {
      errors.push(`File type .${ext} is not supported`);
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  /**
   * Handle file selection from input
   * @param {Event} e - Change event
   */
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
    // Reset input value to allow re-selecting same file
    e.target.value = "";
  };

  /**
   * Add files to selection with validation
   * @param {File[]} files - Files to add
   */
  const addFiles = useCallback((files) => {
    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      const validation = validateFile(file);
      if (validation.isValid) {
        // Check for duplicates
        const isDuplicate = selectedFiles.some(
          (f) => f.name === file.name && f.size === file.size,
        );
        if (!isDuplicate) {
          validFiles.push(file);
        }
      } else {
        errors.push(`${file.name}: ${validation.errors.join(", ")}`);
      }
    });

    if (errors.length > 0) {
      setErrorMessage(errors.join("\n"));
      setTimeout(() => setErrorMessage(""), 5000);
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
  }, [selectedFiles]);

  /**
   * Handle drag over event
   * @param {DragEvent} e - Drag event
   */
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  /**
   * Handle drag leave event
   * @param {DragEvent} e - Drag event
   */
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  /**
   * Handle file drop
   * @param {DragEvent} e - Drop event
   */
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      addFiles(files);
    },
    [addFiles],
  );

  /**
   * Remove file from selection
   * @param {number} index - Index of file to remove
   */
  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    // Clear progress for removed file
    setUploadProgress((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  /**
   * Handle upload process
   * Uploads files to Cloudinary, then sends URLs to Firebase
   */
  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !selectedProjectId) return;

    setUploadStatus("uploading");
    setErrorMessage("");

    // Create Sentry span for upload tracking
    return Sentry.startSpan(
      {
        op: "file.upload",
        name: "Plans & Documents Upload",
      },
      async (span) => {
        span.setAttribute("category", selectedCategory);
        span.setAttribute("field", selectedCategory);
        span.setAttribute("fileCount", selectedFiles.length);
        span.setAttribute("projectId", selectedProjectId);

        try {
          const results = [];

          // Upload each file to Cloudinary
          for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];

            try {
              // Update status to uploading
              setUploadProgress((prev) => ({
                ...prev,
                [i]: { status: "uploading", progress: 0 },
              }));

              // Build standardized Cloudinary context for this upload
              const categoryInfo = DOCUMENT_CATEGORIES.find(
                (c) => c.value === selectedCategory,
              );
              const uploadContext = buildUploadContext({
                projectId: selectedProjectId,
                projectName: selectedProjectName,
                category: selectedCategory,
                categoryLabel: categoryInfo?.label || selectedCategory,
                sourceTable: "projects",
                uploadedByName: teamMemberName,
                file,
              });

              // Upload to Cloudinary
              const cloudinaryResult = await uploadFile(file, {
                projectId: selectedProjectId,
                sourceTable: "projects",
                category: selectedCategory,
                uploadContext,
              });

              // Add to results with Cloudinary URL
              results.push({
                url: cloudinaryResult.url,
                filename: file.name,
              });

              // Mark file as complete
              setUploadProgress((prev) => ({
                ...prev,
                [i]: { status: "complete", progress: 100 },
              }));
            } catch (fileError) {
              console.error(`Error uploading ${file.name}:`, fileError);
              Sentry.captureException(fileError);

              // Mark file as error
              setUploadProgress((prev) => ({
                ...prev,
                [i]: { status: "error", progress: 0 },
              }));
            }
          }

          // If no files uploaded successfully, throw error
          if (results.length === 0) {
            throw new Error("No files were uploaded successfully.");
          }

          // Send URLs to Firebase function to save to Airtable
          const uploadProjectDocument = httpsCallable(
            functions,
            "uploadProjectDocument",
          );

          const result = await uploadProjectDocument({
            projectId: selectedProjectId,
            category: selectedCategory,
            files: results,
          });

          if (!result.data?.success) {
            throw new Error(
              result.data?.message || "Failed to save documents to project",
            );
          }

          // Success!
          setUploadStatus("success");
          span.setAttribute("success", true);

          // Call completion callback
          if (onUploadComplete) {
            onUploadComplete({
              category: selectedCategory,
              fileCount: results.length,
              projectId: selectedProjectId,
              projectName: selectedProjectName,
            });
          }

          // Auto-close after success
          setTimeout(() => {
            handleClose();
          }, 1500);
        } catch (error) {
          console.error("[PlansDocsUploadModal] Upload error:", error);
          Sentry.captureException(error);
          span.setAttribute("success", false);
          span.setAttribute("error", error.message);

          setUploadStatus("error");
          setErrorMessage(error.message || "Failed to upload documents");
        }
      },
    );
  };

  /**
   * Handle modal close
   * Resets state and calls onClose
   */
  const handleClose = () => {
    if (uploadStatus === "uploading") return; // Prevent closing during upload

    // Reset file selection state
    setSelectedCategory("floorplans");
    setSelectedFiles([]);
    setUploadStatus("idle");
    setUploadProgress({});
    setErrorMessage("");

    // Reset project selection only if not locked
    if (!isProjectLocked) {
      setSelectedProjectId("");
      setSelectedProjectName("");
    }

    onClose();
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="pdum-overlay" onClick={handleClose}>
      <div className="pdum-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="pdum-header">
          <div className="pdum-header-left">
            <div className="pdum-header-icon">
              <FaUpload />
            </div>
            <h3>Upload Project Documents</h3>
          </div>
          <button
            className="pdum-close-btn"
            onClick={handleClose}
            disabled={uploadStatus === "uploading"}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="pdum-body">
          {/* Project Selection */}
          <div className="pdum-form-section">
            <label className="pdum-section-label">
              <FaHome /> Project <span className="required">*</span>
            </label>
            {isProjectLocked ? (
              // Locked/prefilled project display
              <div className="pdum-project-locked">
                <span className="pdum-project-locked-name">
                  {selectedProjectName || "Selected Project"}
                </span>
              </div>
            ) : (
              // Project dropdown selector
              <div className="pdum-project-selector">
                {projectsLoading ? (
                  <div className="pdum-project-loading">
                    <FaSpinner className="pdum-spinner" />
                    <span>Loading projects...</span>
                  </div>
                ) : projectsError ? (
                  <div className="pdum-project-error">
                    <FaExclamationCircle />
                    <span>{projectsError}</span>
                    <button
                      type="button"
                      className="pdum-retry-btn"
                      onClick={fetchActiveProjects}
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <select
                    className="pdum-project-select"
                    value={selectedProjectId}
                    onChange={handleProjectChange}
                    disabled={uploadStatus === "uploading"}
                  >
                    <option value="">Select a project...</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.projectName || project.name}
                        {project.projectAddress &&
                          ` - ${project.projectAddress}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {/* Category Selection */}
          <div className="pdum-form-section">
            <label className="pdum-section-label">
              <FaFolderOpen /> Document Category{" "}
              <span className="required">*</span>
            </label>
            <div className="pdum-category-options">
              {DOCUMENT_CATEGORIES.map((category) => {
                const IconComponent = category.icon;
                return (
                  <button
                    key={category.value}
                    type="button"
                    className={`pdum-category-btn ${
                      selectedCategory === category.value ? "active" : ""
                    }`}
                    onClick={() => setSelectedCategory(category.value)}
                    disabled={uploadStatus === "uploading"}
                  >
                    <IconComponent className="pdum-project-icon" />
                    <span className="pdum-category-label">
                      {category.label}
                    </span>
                    <span className="pdum-category-desc">
                      {category.description}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="pdum-form-section">
              <div className="pdum-files-header">
                <span className="pdum-section-label">
                  Selected Files ({selectedFiles.length})
                </span>
                {uploadStatus === "idle" && selectedFiles.length > 1 && (
                  <button
                    className="pdum-clear-all-btn"
                    onClick={() => setSelectedFiles([])}
                  >
                    Clear All
                  </button>
                )}
              </div>

              <div className="pdum-files-list">
                {selectedFiles.map((file, index) => {
                  const progress = uploadProgress[index];
                  return (
                    <div
                      key={`${file.name}-${index}`}
                      className={`pdum-file-item ${
                        progress?.status || "pending"
                      }`}
                    >
                      {getFileIcon(file.name)}
                      <div className="pdum-file-info">
                        <span className="pdum-file-name">{file.name}</span>
                        <span className="pdum-file-size">
                          {formatFileSize(file.size)}
                        </span>
                      </div>
                      <div className="pdum-file-status">
                        {progress?.status === "uploading" && (
                          <FaSpinner className="pdum-spinner" />
                        )}
                        {progress?.status === "complete" && (
                          <FaCheckCircle className="pdum-success-icon" />
                        )}
                        {progress?.status === "error" && (
                          <FaExclamationCircle className="pdum-error-icon" />
                        )}
                        {!progress && uploadStatus === "idle" && (
                          <button
                            className="pdum-remove-btn"
                            onClick={() => handleRemoveFile(index)}
                            aria-label={`Remove ${file.name}`}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Drop Zone */}
          <div className="pdum-form-section">
            <label className="pdum-section-label">
              <FaFileAlt /> Files <span className="required">*</span>
            </label>
            <div
              className={`pdum-dropzone ${isDragging ? "dragging" : ""} ${
                uploadStatus === "uploading" ? "disabled" : ""
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="plans-docs-upload"
                className="pdum-file-input"
                accept={ACCEPTED_FILE_TYPES}
                multiple
                onChange={handleFileSelect}
                disabled={uploadStatus === "uploading"}
              />
              <label
                htmlFor="plans-docs-upload"
                className="pdum-dropzone-content"
              >
                <FaCloudUploadAlt className="pdum-dropzone-icon" />
                <p className="pdum-dropzone-text">
                  Drag & drop files here or click to browse
                </p>
                <p className="pdum-dropzone-hint">
                  PDF, JPG, PNG, WebP • Max 10MB per file
                </p>
              </label>
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="pdum-error-message">
              <FaExclamationCircle />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Success Message */}
          {uploadStatus === "success" && (
            <div className="pdum-success-message">
              <FaCheckCircle />
              <span>Documents uploaded successfully!</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pdum-footer">
          <button
            className="pdum-cancel-btn"
            onClick={handleClose}
            disabled={uploadStatus === "uploading"}
          >
            Cancel
          </button>
          <button
            className="pdum-upload-btn"
            onClick={handleUpload}
            disabled={
              selectedFiles.length === 0 ||
              !selectedProjectId ||
              uploadStatus === "uploading" ||
              uploadStatus === "success"
            }
          >
            {uploadStatus === "uploading" ? (
              <>
                <FaSpinner className="pdum-btn-spinner" />
                Uploading...
              </>
            ) : (
              <>
                <FaUpload />
                {isProjectLocked && selectedFiles.length > 0
                  ? `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? "s" : ""} to `
                  : "Upload to "}
                {!isProjectLocked && selectedProjectName
                  ? `${selectedProjectName}'s `
                  : ""}
                {DOCUMENT_CATEGORIES.find((c) => c.value === selectedCategory)
                  ?.label || "Documents"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlansDocsUploadModal;
